import { PDFDocument, StandardFonts, rgb } from 'pdf-lib'
import PptxGenJS from 'pptxgenjs'
import * as XLSX from 'xlsx'

export type OperatingArtifactSpec = {
  artifact_id: string
  title: string
  freshness_token: string
  company?: { display_name?: string }
  sections: Array<{
    section_id: string
    title: string
    kind: string
    content: Record<string, unknown>
    evidence_bindings: Array<unknown>
  }>
}

function text(value: unknown, max = 500): string {
  if (value == null) return ''
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value).slice(0, max)
  try { return JSON.stringify(value).slice(0, max) } catch { return '' }
}

function safeName(value: string): string {
  return value.replace(/[^a-z0-9 _-]+/gi, '').trim().replace(/\s+/g, '-').slice(0, 100) || 'FuseGrid-Operating-Review'
}

function save(bytes: Uint8Array, contentType: string, fileName: string) {
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: contentType }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  anchor.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 2_000)
}

export function downloadOperatingXlsx(spec: OperatingArtifactSpec) {
  const workbook = XLSX.utils.book_new()
  const rows: unknown[][] = [
    [spec.title],
    ['Company', spec.company?.display_name || ''],
    ['Artifact', spec.artifact_id],
    ['Freshness', spec.freshness_token],
    [],
    ['Section', 'Field', 'Value'],
  ]
  for (const section of spec.sections) {
    rows.push([section.title, 'Evidence records', section.evidence_bindings.length])
    for (const [field, value] of Object.entries(section.content || {})) rows.push([section.title, field.replaceAll('_', ' '), text(value, 1_000)])
  }
  const sheet = XLSX.utils.aoa_to_sheet(rows)
  sheet['!cols'] = [{ wch: 30 }, { wch: 32 }, { wch: 90 }]
  XLSX.utils.book_append_sheet(workbook, sheet, 'Operating Review')
  const bytes = new Uint8Array(XLSX.write(workbook, { type: 'array', bookType: 'xlsx', compression: true }))
  save(bytes, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', `${safeName(spec.title)}.xlsx`)
}

export async function downloadOperatingPptx(spec: OperatingArtifactSpec) {
  const deck = new PptxGenJS()
  deck.layout = 'LAYOUT_WIDE'
  deck.author = 'FuseGrid'
  deck.subject = 'Evidence-bound operating review'
  deck.title = spec.title
  const cover = deck.addSlide()
  cover.background = { color: '163E52' }
  cover.addText('FUSEGRID OPERATING REVIEW', { x: .7, y: .7, w: 6, h: .3, fontSize: 11, bold: true, color: 'A9D6E5', charSpacing: 2 })
  cover.addText(spec.title, { x: .7, y: 2, w: 11, h: 1.4, fontSize: 34, bold: true, color: 'FFFFFF', margin: 0 })
  cover.addText(`${spec.company?.display_name || 'Client'}\nLive evidence version · ${spec.freshness_token.slice(0, 40)}`, { x: .72, y: 4.25, w: 9, h: .8, fontSize: 13, color: 'DCE8EE', margin: 0 })
  for (const section of spec.sections) {
    const slide = deck.addSlide()
    slide.background = { color: 'F7F5F0' }
    slide.addShape(deck.ShapeType.rect, { x: 0, y: 0, w: .16, h: 7.5, fill: { color: '1B4965' }, line: { color: '1B4965' } })
    slide.addText(section.title, { x: .7, y: .55, w: 11.8, h: .55, fontSize: 26, bold: true, color: '17252F', margin: 0 })
    const lines = Object.entries(section.content || {}).slice(0, 9).map(([key, value]) => ({
      text: `${key.replaceAll('_', ' ')}: ${text(value, 260)}`,
      options: { bullet: { indent: 18 }, breakLine: true, color: '43535C', fontSize: 13, paraSpaceAfterPt: 9 },
    }))
    slide.addText(lines.length ? lines : 'No additional detail', { x: .85, y: 1.55, w: 11.4, h: 4.9, valign: 'top', margin: .06 })
    slide.addText(`${section.evidence_bindings.length} evidence bindings · ${spec.artifact_id}`, { x: .72, y: 6.95, w: 11.5, h: .2, fontSize: 8, color: '75838A', margin: 0 })
  }
  const output = await deck.write({ outputType: 'arraybuffer', compression: true })
  save(new Uint8Array(output as ArrayBuffer), 'application/vnd.openxmlformats-officedocument.presentationml.presentation', `${safeName(spec.title)}.pptx`)
}

function wrap(value: string, max = 92): string[] {
  const lines: string[] = []
  let current = ''
  for (const word of value.split(/\s+/)) {
    if (`${current} ${word}`.trim().length > max && current) { lines.push(current); current = word } else current = `${current} ${word}`.trim()
  }
  if (current) lines.push(current)
  return lines
}

export async function downloadOperatingPdf(spec: OperatingArtifactSpec) {
  const pdf = await PDFDocument.create()
  const regular = await pdf.embedFont(StandardFonts.Helvetica)
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold)
  const pageFor = (title: string, subtitle: string) => {
    const page = pdf.addPage([612, 792])
    page.drawRectangle({ x: 0, y: 744, width: 612, height: 48, color: rgb(.07, .22, .29) })
    page.drawText('FUSEGRID', { x: 42, y: 763, size: 10, font: bold, color: rgb(.78, .9, .94) })
    page.drawText(title.slice(0, 70), { x: 42, y: 690, size: 24, font: bold, color: rgb(.08, .13, .16), maxWidth: 528 })
    page.drawText(subtitle, { x: 42, y: 660, size: 9, font: regular, color: rgb(.36, .43, .46), maxWidth: 528 })
    return page
  }
  const cover = pageFor(spec.title, `${spec.company?.display_name || 'Client'} · Evidence-bound operating review`)
  cover.drawText('Prepared from the same governed version used by the live workspace.', { x: 42, y: 590, size: 14, font: regular, color: rgb(.16, .27, .32), maxWidth: 500 })
  cover.drawText(`Artifact ${spec.artifact_id}\nFreshness ${spec.freshness_token}`, { x: 42, y: 120, size: 8, font: regular, color: rgb(.4, .47, .5), lineHeight: 12 })
  for (const section of spec.sections) {
    const page = pageFor(section.title, section.kind.replaceAll('_', ' '))
    let y = 615
    for (const [key, value] of Object.entries(section.content || {}).slice(0, 12)) {
      page.drawText(key.replaceAll('_', ' ').toUpperCase(), { x: 42, y, size: 8, font: bold, color: rgb(.1, .29, .4) })
      y -= 18
      for (const line of wrap(text(value, 700))) { page.drawText(line, { x: 42, y, size: 10, font: regular, color: rgb(.18, .25, .28) }); y -= 14; if (y < 80) break }
      y -= 10
      if (y < 80) break
    }
  }
  save(await pdf.save(), 'application/pdf', `${safeName(spec.title)}.pdf`)
}
